import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaClipboardList, FaExternalLinkAlt, FaFileAlt, FaInfoCircle } from 'react-icons/fa';
// Clinician survey is temporarily hidden.
// import ClinicianSurveyButton from './ClinicianSurveyButton';

interface ReportActionsProps {
  onInsuranceInfo: () => void;
  onMedicalNecessity: () => void;
  onWorkflowInfo: () => void;
  isProvider?: boolean;
  surveyComplete?: boolean;
  onOpenSurvey?: () => void;
}

export const HowToUseThisPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section className="report-section report-how-to-use">
      <div className="report-card__header">
        <div>
          <p className="report-eyebrow">{t('workflow.eyebrow')}</p>
          <h3 className="report-card__title">{t('workflow.title')}</h3>
        </div>
      </div>

      <div className="report-bullet-list">
        <div className="report-bullet-item">{t('workflow.item1')}</div>
        <div className="report-bullet-item">{t('workflow.item2')}</div>
      </div>
    </section>
  );
};

/**
 * Report actions and resources component
 */
const ReportActions: React.FC<ReportActionsProps> = ({
  onInsuranceInfo,
  onMedicalNecessity,
  onWorkflowInfo,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [copiedPos, setCopiedPos] = useState<{ x: number; y: number } | null>(null);
  const supportRef = React.useRef<HTMLDivElement>(null);

  const copyFallback = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    } finally {
      document.body.removeChild(textArea);
    }
    return ok;
  };

  const handleCopySupportEmail = async (
    e?: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>
  ) => {
    const email = 'mamabear@loonmd.com';
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(email);
        ok = true;
      } else {
        ok = copyFallback(email);
      }
    } catch {
      ok = copyFallback(email);
    }
    // Capture cursor/touch position relative to the support section for floating tooltip
    if (supportRef.current && e) {
      let clientX: number | null = null;
      let clientY: number | null = null;
      // Touch first
      // @ts-expect-error Narrowing for React TouchEvent
      if (e.touches && e.touches[0]) {
        // @ts-expect-error TouchEvent typing
        clientX = e.touches[0].clientX;
        // @ts-expect-error TouchEvent typing
        clientY = e.touches[0].clientY;
      } else {
        const me = e as React.MouseEvent<HTMLButtonElement>;
        clientX = (me && me.clientX) || null;
        clientY = (me && me.clientY) || null;
      }
      if (clientX != null && clientY != null) {
        const rect = supportRef.current.getBoundingClientRect();
        setCopiedPos({ x: clientX - rect.left, y: clientY - rect.top });
      } else {
        setCopiedPos(null);
      }
    } else {
      setCopiedPos(null);
    }
    setCopied(ok);
    window.setTimeout(() => {
      setCopied(false);
      setCopiedPos(null);
    }, 1600);
  };
  return (
    <div className="space-y-4">
      {/*
      Clinician survey is temporarily hidden.
      {isProvider && onOpenSurvey && (
        <ClinicianSurveyButton
          onClick={onOpenSurvey}
          isComplete={surveyComplete}
        />
      )}
      */}

      <section id="provider-resources" className="report-section">
        <div className="report-card__header">
          <div>
            <p className="report-eyebrow">{t('resources.eyebrow')}</p>
            <h3 className="report-card__title">{t('resources.title')}</h3>
          </div>
        </div>

        <div className="report-resource-list">
          <button
            type="button"
            onClick={onInsuranceInfo}
            className="report-resource-link"
            title={t('resources.insuranceTitle')}
            aria-label={t('resources.insuranceTitle')}
          >
            <span className="report-resource-link__body">
              <FaClipboardList className="report-resource-link__icon" title={t('resources.insuranceTitle')} />
              <span>
                <span className="report-resource-link__title">{t('resources.insuranceTitle')}</span>
                <span className="report-resource-link__detail">{t('resources.insuranceDetail')}</span>
              </span>
            </span>
            <FaExternalLinkAlt className="text-[var(--text-tertiary)]" title={t('resources.externalTitle')} />
          </button>

          <button
            type="button"
            onClick={onMedicalNecessity}
            className="report-resource-link"
            title={t('resources.medicalTitle')}
            aria-label={t('resources.medicalTitle')}
          >
            <span className="report-resource-link__body">
              <FaFileAlt className="report-resource-link__icon" title={t('resources.medicalTitle')} />
              <span>
                <span className="report-resource-link__title">{t('resources.medicalTitle')}</span>
                <span className="report-resource-link__detail">{t('resources.medicalDetail')}</span>
              </span>
            </span>
            <FaExternalLinkAlt className="text-[var(--text-tertiary)]" title={t('resources.externalTitle')} />
          </button>

          <button
            type="button"
            onClick={onWorkflowInfo}
            className="report-resource-link"
            title={t('resources.workflowTitle')}
            aria-label={t('resources.workflowTitle')}
          >
            <span className="report-resource-link__body">
              <FaInfoCircle className="report-resource-link__icon" title={t('resources.workflowTitle')} />
              <span>
                <span className="report-resource-link__title">{t('resources.workflowTitle')}</span>
                <span className="report-resource-link__detail">{t('resources.workflowDetail')}</span>
              </span>
            </span>
            <FaExternalLinkAlt className="text-[var(--text-tertiary)]" title={t('resources.externalTitle')} />
          </button>
        </div>
      </section>

      <section id="support" className="report-section report-support" ref={supportRef}>
        <div className="report-card__header">
          <div>
            <p className="report-eyebrow">{t('support.eyebrow')}</p>
            <h3 className="report-card__title">{t('support.title')}</h3>
          </div>
        </div>

        <p className="report-card__subtitle">
          {t('support.subtitle')}
        </p>
        <button
          type="button"
          onClick={(e) => handleCopySupportEmail(e)}
          onTouchStart={(e) => handleCopySupportEmail(e)}
          className="report-copy-link mt-4 inline-flex text-base font-medium"
          title={t('support.copyTitle')}
          aria-label={t('support.copyAria')}
        >
          mamabear@loonmd.com
        </button>
        {copied && copiedPos && (
          <span
            className="pointer-events-none absolute z-50 rounded bg-black px-2 py-1 text-xs text-white shadow"
            style={{ left: copiedPos.x, top: copiedPos.y, transform: 'translate(-50%, -125%)' }}
          >
            {t('common.copied')}
          </span>
        )}
        <span className="sr-only" aria-live="polite">
          {copied ? t('common.copied') : ''}
        </span>
      </section>

      <section id="mobile-app" className="report-section text-center">
        <div className="report-card__header">
          <div className="w-full">
            <p className="report-eyebrow">{t('mobileApp.eyebrow')}</p>
            <h3 className="report-card__title">{t('mobileApp.title')}</h3>
          </div>
        </div>

        <p className="report-card__subtitle mx-auto max-w-sm">
          {t('mobileApp.subtitle')}
        </p>

        <div className="report-store-links">
          <a
            href="https://play.google.com/store/apps/details?id=loonmd.com.mama_bear_fe"
            target="_blank"
            rel="noopener noreferrer"
            className="report-store-link"
          >
            <span>Android</span>
            <span className="report-store-link__badge">Google Play</span>
          </a>
          <a
            href="https://apps.apple.com/us/app/mamabear-health/id1668417091"
            target="_blank"
            rel="noopener noreferrer"
            className="report-store-link report-store-link--ios"
          >
            <span>iOS</span>
            <span className="report-store-link__badge">App Store</span>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ReportActions;
